import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import EditableSection from 'components/v2/EditableSection';
import { useTranslation } from 'react-i18next';
import LabelValue from 'components/ui/tailwind/label-value';
import Checkbox from 'components/v2/forms/Checkbox';
import SessionStore from 'stores/SessionStore';
import {
  sortableContainer,
  sortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import Spinner from 'components/ui/Spinner';
import { showToast } from 'stores/ToastStore';
import { checkPermissonForEditShipperBillingModule } from '../utils';
import { useGetDocumentTypes } from 'utils/DocumentsAPIUtils';
import { LoaderCircle } from 'lucide-react';

const DrayageInvoiceDocumentPreferencesSummary = ({
  fields = [],
  invoiceDocumentPreferenceDocTypesMap,
}) => {
  const { t } = useTranslation();
  return (
    <div className="pb-4">
      {!fields.length ? (
        <Spinner showMessage={false} />
      ) : (
        fields.map((pref) => (
          <LabelValue
            key={pref.slug}
            sequence={pref.sequence}
            value={t(
              invoiceDocumentPreferenceDocTypesMap[pref.slug]?.localizationSlug,
            )}
          />
        ))
      )}
    </div>
  );
};

const DrayageInvoiceDocumentPreferencesForm = ({
  fields,
  saving,
  onChange,
  onSaveShipper,
  onChangePreferences,
  onSaveSuccess,
  onSaveFail,
  invoiceDocumentPreferenceDocTypesMap,
  invoiceDocumentPreferenceDocTypes,
}) => {
  const { t } = useTranslation();
  const [
    invoiceDocumentPreferenceDocTypesWithDisplayLabelSorted,
    setInvoiceDocumentPreferenceDocTypesWithDisplayLabelSorted,
  ] = useState([]);

  useEffect(() => {
    prepareInvoiceDocumentsToDisplay(invoiceDocumentPreferenceDocTypes);
  }, [invoiceDocumentPreferenceDocTypes]);

  const invoiceDocumentPreferences = fields || [];

  const SortableContainer = sortableContainer(({ children }) => {
    return <tbody>{children}</tbody>;
  });

  const SortableItem = sortableElement(({ children }) => (
    <tr className="cursor-pointer">{children}</tr>
  ));

  const isChecked = (slug) => {
    return !!invoiceDocumentPreferences.find((pref) => pref?.slug === slug);
  };

  const requiredInvoiceDocTypes = ['shipment_invoice'];

  const isDisabled = (type) => {
    if (!SessionStore.hasFinanceRole()) {
      return true;
    }
    return requiredInvoiceDocTypes.includes(type);
  };

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    const updatedPreferences = arrayMove(
      invoiceDocumentPreferences,
      oldIndex,
      newIndex,
    ).map((pref, i) => {
      const { slug } = pref;
      const sequence = i + 1;
      return {
        slug,
        sequence,
      };
    });
    onChangePreferences(updatedPreferences);
  };

  const handleRemove = (slug) => {
    const updatedPreferences = invoiceDocumentPreferences
      .filter((pref) => pref.slug !== slug)
      .map((pref, i) => ({ ...pref, sequence: i + 1 }));
    onChangePreferences(updatedPreferences);
  };

  const saveDisabled = () => {
    return !SessionStore.hasFinanceRole();
  };
  const saveForm = async () => {
    try {
      await onSaveShipper();
      showToast(t('SHIPPER_UPDATED'));
      onSaveSuccess(fields);
    } catch (e) {
      showToast(e.message, '', 'error');
      onSaveFail();
    }
  };

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  function prepareInvoiceDocumentsToDisplay(invoiceDocumentPreferenceDocTypes) {
    // first, get translated slug and set it as displayLabel
    const invoiceDocumentPreferenceDocTypesWithDisplayLabel =
      invoiceDocumentPreferenceDocTypes.map((doc) => ({
        ...doc,
        displayLabel: t(doc.localizationSlug, { count: doc.count }),
      }));
    // sort the documents for display by the translation label
    const sorted =
      invoiceDocumentPreferenceDocTypesWithDisplayLabel.sort(
        sortDocumentBySlug,
      );
    setInvoiceDocumentPreferenceDocTypesWithDisplayLabelSorted(sorted);
  }

  function sortDocumentBySlug(a, b) {
    if (a.value === 'shipment_invoice') return -1; // invoice will always be first

    if (a.displayLabel < b.displayLabel) {
      return -1;
    }
    if (b.displayLabel > a.displayLabel) {
      return 1;
    }
    return 0;
  }

  return invoiceDocumentPreferences.length ? (
    <div>
      <h4 className="my-0 mb-1">{t('DOCUMENT_TYPES')}</h4>
      <p className="text-sm mb-4">{t('PLEASE_SELECT_SEQ_BELOW')}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {invoiceDocumentPreferenceDocTypesWithDisplayLabelSorted.map(
          (docType) => (
            <Checkbox
              key={docType.type}
              checked={isChecked(docType.type)}
              disabled={isDisabled(docType.type)}
              label={docType.displayLabel}
              onChange={onChange(docType.type)}
              id={docType.type}
            />
          ),
        )}
      </div>
      <hr className="my-4" />
      <div className="overflow-x-auto mt-4 overyflow-y-auto">
        <table className="default-table">
          <thead>
            <tr>
              <th>{t('DOCUMENT')}</th>
              <th>{t('ACTIONS')}</th>
            </tr>
          </thead>
          <SortableContainer onSortEnd={handleSortEnd} distance={10}>
            {invoiceDocumentPreferences.map((pref, i) => {
              const { slug, sequence } = pref || {};
              const key = `${slug}-${sequence}`;
              const docType = invoiceDocumentPreferenceDocTypesMap[slug];
              return (
                <SortableItem key={key} index={i} disabled={saveDisabled()}>
                  <td>
                    <div className="flex flex-wrap items-center space-x-2">
                      <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 inline-flex items-center justify-center">
                        <span>{sequence}</span>
                      </div>
                      <span className="flex-1 text-sm text-gray-800">
                        {t(docType?.localizationSlug)}
                      </span>
                    </div>
                  </td>
                  <td>
                    {!isDisabled(slug) && (
                      <DeleteButton
                        onClick={() => handleRemove(slug)}
                        size="small"
                      />
                    )}
                  </td>
                </SortableItem>
              );
            })}
          </SortableContainer>
        </table>
      </div>
    </div>
  ) : (
    <Spinner showMessage={false} />
  );
};

const DrayageInvoiceDocumentPreferences = ({
  data,
  onUpdateShipper,
  className,
}) => {
  const { t } = useTranslation();
  const [preferencesData, setPreferencesData] = useState(data);
  const [initialData, setInitialData] = useState(data);

  const { data: invoiceDocumentData, isLoading } = useGetDocumentTypes({
    listType: 'invoice',
    showListType: true,
  });
  const invoiceDocumentPreferenceDocTypes = useMemo(
    () => invoiceDocumentData?.getAvailableDocumentTypes || [],
    [invoiceDocumentData],
  );

  const invoiceDocumentPreferenceDocTypesMap = useMemo(
    () =>
      invoiceDocumentPreferenceDocTypes.reduce(
        (map, pref) => ({
          ...map,
          [pref.type]: pref,
        }),
        {},
      ),
    [invoiceDocumentPreferenceDocTypes],
  );

  const getInitData = () => {
    if (data) {
      setPreferencesData(data);
      setInitialData(data);
    }
  };
  useEffect(() => {
    getInitData();
  }, [data]);

  const onChange = (slug) => {
    return (checked) => {
      if (!checked) {
        setPreferencesData((prev) => {
          const updated = prev
            .filter((pref) => pref.slug !== slug)
            .map((pref, i) => ({ ...pref, sequence: i + 1 }));
          return [...updated];
        });
      } else {
        setPreferencesData((prev) => {
          const sequence = prev.length + 1;
          return [...prev, { slug, sequence }];
        });
      }
    };
  };

  const handleSetPreferences = (updates = []) => {
    setPreferencesData([...updates]);
  };

  const handleSave = async () => {
    let isValid = false;
    if (initialData.length === preferencesData.length) {
      const diff = preferencesData.filter(
        ({ slug: prefSlug }) =>
          !initialData.some(({ slug: initSlug }) => initSlug === prefSlug),
      );
      if (diff.length) {
        isValid = true;
      }
    } else if (initialData.length !== preferencesData.length) {
      isValid = true;
    }

    if (isValid) {
      await onUpdateShipper(preferencesData);
    }
  };

  const handleAfterSave = (onSaveSuccess) => {
    return (updatedData = {}) => {
      setInitialData([...updatedData]);
      onSaveSuccess();
    };
  };

  if (isLoading) {
    return <LoaderCircle className="w-4 h-4 animate-spin ml-2" />;
  }

  return (
    <EditableSection
      title={t('INVOICE_DOCUMENT_PREFERENCES')}
      className={classNames(className)}
      canEdit={checkPermissonForEditShipperBillingModule()}
    >
      {(editing, saving, onSaveSuccess, onSaveFail) => (
        <>
          {!editing && (
            <DrayageInvoiceDocumentPreferencesSummary
              fields={initialData}
              invoiceDocumentPreferenceDocTypesMap={
                invoiceDocumentPreferenceDocTypesMap
              }
            />
          )}
          {editing && (
            <DrayageInvoiceDocumentPreferencesForm
              fields={preferencesData}
              onChange={onChange}
              onChangePreferences={handleSetPreferences}
              onSaveShipper={() => handleSave()}
              saving={saving}
              onSaveSuccess={handleAfterSave(onSaveSuccess)}
              onSaveFail={onSaveFail}
              invoiceDocumentPreferenceDocTypesMap={
                invoiceDocumentPreferenceDocTypesMap
              }
              invoiceDocumentPreferenceDocTypes={
                invoiceDocumentPreferenceDocTypes
              }
            />
          )}
        </>
      )}
    </EditableSection>
  );
};

export default DrayageInvoiceDocumentPreferences;
